import { useEffect, useEffectEvent, useState } from 'react';

import { t } from '@web/i18n';
import type { FlightAlertsFilters } from '@web/data/repositories';
import { AppIcons, FilterSelect, Input, Switch, useDebouncedValue } from '@web/ui/core';

interface AlertFiltersProps {
  filters: FlightAlertsFilters;
  onChange: (next: FlightAlertsFilters) => void;
}

// kanban-api clamps `q` at max(80) and 400s above it — keep the input in sync.
const SEARCH_MAX_LENGTH = 80;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Toolbar above the alerts table: severity select, VIP switch, and a
 * debounced free-text search (name, flight or voucher). Each control calls
 * `onChange` with the full next `FlightAlertsFilters` — the page owns the
 * single source of truth and refetches on change.
 */
export function AlertFilters({
  filters,
  onChange,
}: Readonly<AlertFiltersProps>): React.JSX.Element {
  const [searchInput, setSearchInput] = useState(filters.q);
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // Only the debounced value should re-trigger this: `filters`/`onChange`
  // change identity on every keystroke-driven parent render, which would
  // otherwise re-fire the effect (and the fetch) on every keystroke. The
  // Effect Event reads their latest values without being a dependency.
  const commitSearch = useEffectEvent((q: string) => {
    if (q === filters.q) return;
    onChange({ ...filters, q });
  });
  useEffect(() => {
    commitSearch(debouncedSearch);
  }, [debouncedSearch]);

  return (
    <div className="flex items-center gap-2">
      <div className="flex max-w-search-md flex-1">
        <Input
          size="sm"
          value={searchInput}
          onChange={(e) => {
            setSearchInput(e.target.value.slice(0, SEARCH_MAX_LENGTH));
          }}
          placeholder={t('alerts.filters.search')}
          leadingIcon={<AppIcons name="magnifying-glass" size={14} />}
          maxLength={SEARCH_MAX_LENGTH}
        />
      </div>

      <FilterSelect
        label={t('alerts.filters.severity.label')}
        icon="warning"
        value={filters.severity}
        setValue={(severity) => {
          onChange({ ...filters, severity });
        }}
        options={[
          { value: 'all', label: t('alerts.filters.severity.all') },
          { value: 'act', label: t('alerts.filters.severity.act') },
          { value: 'watch', label: t('alerts.filters.severity.watch') },
        ]}
      />

      <label className="inline-flex h-row-h items-center gap-2 rounded-md border border-border bg-card px-2.5 text-meta text-foreground">
        <Switch
          checked={filters.vip}
          onCheckedChange={(vip) => {
            onChange({ ...filters, vip });
          }}
          aria-label={t('alerts.filters.vip')}
        />
        {t('alerts.filters.vip')}
      </label>
    </div>
  );
}
